import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, X, ChevronDown } from "lucide-react";
import { readEnv } from "@pgs-core/portal-lib/config";
import {
  fetchGoogleFonts,
  filterFonts,
  buildFontValue,
  parseFontValue,
  type GoogleFont,
} from "../lib/google-fonts";

const MAX_RESULTS = 50;
const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

function getApiKey(): string {
  const env = import.meta.env;
  return readEnv(env, "PUBLIC_GOOGLE_FONTS_API_KEY", "VITE_GOOGLE_FONTS_API_KEY");
}

/**
 * Searchable dropdown for picking a Google Font. Triggers onChange with a full
 * CSS `font-family` value like `"Roboto", sans-serif`.
 */
export function FontPicker({
  value,
  placeholder,
  onChange,
  ariaLabel,
}: {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [fonts, setFonts] = useState<GoogleFont[]>([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GoogleFont[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const parsed = parseFontValue(value);
  const displayName = parsed?.family || "";

  // Load fonts catalogue on first open.
  const loadFonts = useCallback(async () => {
    if (fonts.length > 0) return;
    const key = getApiKey();
    if (!key) {
      setError("Google Fonts API key not configured.");
      return;
    }
    setLoading(true);
    try {
      const fetched = await fetchGoogleFonts(key);
      setFonts(fetched);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [fonts.length]);

  // Open handler.
  const handleOpen = useCallback(() => {
    setOpen(true);
    loadFonts();
    // Focus the search input after the panel renders.
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [loadFonts]);

  // Click-outside & Escape handling.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Debounced filtering.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < MIN_QUERY_LENGTH) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setResults(filterFonts(fonts, query).slice(0, MAX_RESULTS));
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, fonts]);

  const selectFont = (font: GoogleFont) => {
    onChange(buildFontValue(font.family, font.category));
    setOpen(false);
    setQuery("");
    setResults([]);
  };

  const clearFont = () => {
    onChange("");
    setOpen(false);
    setQuery("");
    setResults([]);
  };

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={open ? () => setOpen(false) : handleOpen}
        className="flex h-9 w-full items-center justify-between rounded-md border bg-background px-2 text-xs hover:bg-muted"
      >
        <span className={displayName ? "truncate" : "truncate text-muted-foreground"}>
          {displayName || placeholder || "Select font…"}
        </span>
        <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-md border bg-background shadow-lg">
          {/* Search input */}
          <div className="flex items-center gap-1.5 border-b px-2 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search fonts (min 3 chars)…"
              className="h-7 w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
            />
          </div>

          <div className="max-h-60 overflow-y-auto p-1">
            {error && (
              <p className="px-2 py-3 text-xs text-destructive">{error}</p>
            )}

            {loading && (
              <p className="px-2 py-3 text-xs text-muted-foreground">Loading fonts…</p>
            )}

            {/* Clear option */}
            {displayName && !loading && !error && (
              <button
                type="button"
                onClick={clearFont}
                className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted"
              >
                <X className="h-3 w-3" />
                Clear (inherit default)
              </button>
            )}

            {/* Hint when query is too short */}
            {!loading && !error && query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH && (
              <p className="px-2 py-3 text-xs text-muted-foreground">
                Type at least {MIN_QUERY_LENGTH} characters to search.
              </p>
            )}

            {/* No results */}
            {!loading && !error && query.trim().length >= MIN_QUERY_LENGTH && results.length === 0 && (
              <p className="px-2 py-3 text-xs text-muted-foreground">No fonts found.</p>
            )}

            {/* Results */}
            {results.map((font) => (
              <button
                key={font.family}
                type="button"
                onClick={() => selectFont(font)}
                className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs hover:bg-muted"
              >
                <span className="truncate">{font.family}</span>
                <span className="ml-2 shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {font.category}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
